import React, { Suspense, useEffect, useMemo, useState } from "react";
import { portfolioAnalysisApiSlice, useGetBenchmarkChartDataQuery, useGetBenchmarkingQuery, useGetBenchmarkOverwriteQuery, useGetPortfolioAnalysisHeatQuery, useGetPortfolioAnalysisPowerQuery, useGetPortfolioAnalysisSubscriptionQuery, useGetPortfolioAnalysisWaterQuery } from "../../../api/energy-analysis/portfolioAnalysisApiSlice";
import { CircularProgress, Box, Typography, Card, CardContent, DialogContent, Dialog } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedAccount } from "../../../redux/selectedAccountSlice";
import { showSnackbar } from "../../../redux/snackbarSlice";
import MeterContainer from "./MeterContainer";
import { useLocation } from "react-router-dom";
import { OtherHouses } from "@mui/icons-material";
const PortfolioAnalysisDataGrid = React.lazy(() => import('remote/PortfolioAnalysisDataGrid').catch(() => ({ default: () => <div>Failed to load</div> })));
const PortfolioAnalysisBenchmarkChart = React.lazy(() => import('remote/PortfolioAnalysisBenchmarkChart').catch(() => ({ default: () => <div>Failed to load</div> })));

const PortfolioAnalysisPage = () => {
  const account = useSelector(getSelectedAccount)

  const location = useLocation()


  const analysisType = useMemo(()=> {
    if (location.pathname.includes('heat')) { return 'heat' }
    else if (location.pathname.includes('water')) { return 'water' }
    else { return 'power' }
  })




  const [outOfSync, setOutOfSync] = useState(false);

  const { data: subscriptionData, isLoading: subscriptionLoading, isError: subscriptionError } = useGetPortfolioAnalysisSubscriptionQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId, pollingInterval: 60000, skipPollingIfUnfocused: true });

  useEffect(() => {
    if (subscriptionData?.power_in_sync === true && subscriptionData?.power_in_sync === outOfSync)
    {
      setOutOfSync(false)
    }
    if (subscriptionData?.power_in_sync === false && subscriptionData?.power_in_sync === outOfSync)       
    {
      setOutOfSync(true)
      portfolioAnalysisApiSlice.util.invalidateTags(['PortfolioAnalysis']);
    }
  }, [subscriptionData, subscriptionLoading, subscriptionError]);

  const { data: powerData, error: powerError, isLoading: isPowerLoading, isFetching: isPowerFetching } = useGetPortfolioAnalysisPowerQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId });
  const { data: waterData, error: waterError, isLoading: isWaterLoading, isFetching: isWaterFetching } = useGetPortfolioAnalysisWaterQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId });
  const { data: heatData, error: heatError, isLoading: isHeatLoading, isFetching: isHeatFetching } = useGetPortfolioAnalysisHeatQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId });

  const data = (() => {
    if (analysisType === 'power') return powerData
    if (analysisType === 'heat') return heatData?.portfolio_analysis_list ? { ...heatData, portfolio_analysis_list: heatData.portfolio_analysis_list.map(o => ({ ...o, metering_point_id: JSON.parse(o.data_stream).unit_id })) } : heatData;
    if (analysisType === 'water') return waterData?.portfolio_analysis_list ? { ...waterData, portfolio_analysis_list: waterData.portfolio_analysis_list.map(o => ({ ...o, metering_point_id: JSON.parse(o.data_stream).unit_id })) } : waterData;
  })()

  const error = (() => {
    if (analysisType === 'power') return powerError
    if (analysisType === 'heat') return heatError
    if (analysisType === 'water') return waterError
  })()

  const isLoading = (() => {
    if (analysisType === 'power') return isPowerLoading
    if (analysisType === 'heat') return isHeatLoading
    if (analysisType === 'water') return isWaterLoading
  })()
  const isFetching = (() => {
    if (analysisType === 'power') return isPowerFetching
    if (analysisType === 'heat') return isHeatFetching
    if (analysisType === 'water') return isWaterFetching
  })()

  
  const { data: benchmarkData, isError: benchmarkError, isLoading: benchmarkLoading, isFetching: benchmarkFetching } = useGetBenchmarkingQuery({ recalc_account_id: account?.AccountId }, { skip: !account?.AccountId })
  const { data: benchmarkOverwriteData, isError: isBenchmarkOverwriteError, isLoading: isBenchmarkOverwriteLoading } = useGetBenchmarkOverwriteQuery({ account_id: account?.AccountId }, { skip: !account?.AccountId });

  const enrichedData = useMemo(() => {
    if (data != null && benchmarkData == null) return data
    if (data == null || benchmarkData == null) return null

    let portfolioList = data.portfolio_analysis_list.filter(item => item.main_address)

    let temp = {
      ...data, portfolio_analysis_list: portfolioList.map(o => {
        let benchmarkEntry = benchmarkData.BenchmarkData.find(b => b.MatrikelKey == o.matrikel_key)
        let benchmarkOverwriteEntry = benchmarkOverwriteData?.find(b => b.MatrikelKey == o.matrikel_key) || {}
        return { ...o, ...benchmarkEntry, overwrite: benchmarkOverwriteEntry }
      })
    }
    
    return temp
  }, [data, benchmarkData])

  const [isOpen, setIsOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState({})

  const handleSetSelectedMeter = (meter) => {
    setIsOpen(true);
    setSelectedMeter(meter);
  }

  const [meterType, setMeterType] = useState('')

  const dispatch = useDispatch();

  const [selectedBenchmark, setSelectedBenchmark] = useState({})
  const { data: benchmarkChartData, isError: isBenchmarkChartDataError, isLoading: isBenchmarkChartDataLoading, isFetching: isBenchmarkChartDataFetching } = useGetBenchmarkChartDataQuery({ matrikelKey: selectedBenchmark?.matrikel_key, accountId: account?.AccountId }, { skip: !!!selectedBenchmark?.matrikel_key })

  useEffect(() => {
    if (isBenchmarkChartDataError)
      dispatch(showSnackbar({ message: 'Der skete en uventet fejl ved hentning af data', severity: 'error' }));
  }, [isBenchmarkChartDataError])

  useEffect(() => {
    if (isBenchmarkChartDataError)
      dispatch(showSnackbar({ message: 'Der skete en uventet fejl ved hentning af data', severity: 'error' }));
  }, [isBenchmarkChartDataError])

  return (
    <>
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, maxHeight: "90vh" }}>
        {(isLoading || benchmarkLoading) ? <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', height: '80vh', verticalAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 4 }}>Henter analyse...</Typography>
        </Box> :
          <>
            {(error) && <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
              <Card width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }} variant="outlined" sx={{ p: 2, mb: 2, mt: 8, borderRadius: 2 }}>
                <CardContent width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}>
                  <Typography variant="h6">Ingen analyse fundet</Typography>
                  <Typography variant="body1" color="text.secondary">Der er ikke fundet en analyse på denne konto.</Typography>
                </CardContent>
              </Card>
            </Box>}

            {(enrichedData && !error) && (
              <Suspense fallback={
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', height: '80vh', verticalAlign: 'center' }}>
                  <CircularProgress />
                </Box>}>
                <Box minHeight={'50vh'} display={'flex'} flexDirection={'column'}>

                <PortfolioAnalysisDataGrid
                  data={enrichedData}
                  setSelectedBenchmark={setSelectedBenchmark}
                  setSelectedMeter={handleSetSelectedMeter}
                  setMeterType={setMeterType}
                  outOfSync={outOfSync}
                  analysisType={analysisType}
                  isLoading={isFetching}
                />
                </Box>

              </Suspense>
            )}

            <Dialog
              maxWidth={false}
              open={!!selectedBenchmark?.matrikel_key}
              onClose={() => setSelectedBenchmark({})}
            >
              <DialogContent>
                <Box><Suspense fallback={<Box style={{ width: '60vw', height: '30vh', display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', verticalAlign: 'center' }}><CircularProgress /></Box>}>
                  <PortfolioAnalysisBenchmarkChart data={benchmarkChartData} address={selectedBenchmark.address} isLoading={isBenchmarkChartDataFetching} />
                </Suspense></Box>

              </DialogContent>
            </Dialog>  
          </>
        }
        <MeterContainer
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          meter={selectedMeter}
          meterType={meterType}
          analysisType={analysisType}
        />
      </Box>
    </>
  );
};

export default PortfolioAnalysisPage;
